/**
 * KhiScreeningQuiz — „нужно ли ми е изследване на бъбреците".
 * Данни: GET /api/khi/screening-questions, POST /api/khi/screening-check
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ClipboardCheck, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KhiScreeningQuiz({ cardCls, isDark }) {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/khi/screening-questions`)
      .then(({ data }) => setMeta(data))
      .catch(() => setMeta(null));
  }, []);

  if (!meta?.questions?.length) return null;

  const check = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/khi/screening-check`, answers);
      setResult(data);
    } finally {
      setBusy(false);
    }
  };

  const color = !result ? "#94A3B8"
    : !result.needs_testing ? "#10B981"
      : result.urgency === "high" ? "#EF4444" : "#F59E0B";

  return (
    <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="khi-screening-card">
      <button onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="khi-screening-toggle">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-teal-500" />
            Нужно ли ми е изследване на бъбреците
          </h3>
          <p className="text-[11px] opacity-60 mt-0.5">{meta.intro_bg}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 opacity-50" /> : <ChevronDown className="w-4 h-4 opacity-50" />}
      </button>

      {open && (
        <>
          <div className="mt-3 space-y-1.5" data-testid="khi-screening-questions">
            {meta.questions.map((q) => (
              <div key={q.code}
                className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2
                            ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
                <span className="text-[12px]">{q.q_bg}</span>
                <div className="flex gap-1 shrink-0">
                  {[["да", true], ["не", false]].map(([label, val]) => (
                    <button key={label}
                      onClick={() => setAnswers((a) => ({ ...a, [q.code]: val }))}
                      className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border transition-colors
                        ${answers[q.code] === val
                          ? (val ? "bg-teal-500 text-white border-teal-500"
                            : "bg-slate-400 text-white border-slate-400")
                          : isDark ? "border-slate-700" : "border-slate-300"}`}
                      data-testid={`khi-screening-${q.code}-${val ? "yes" : "no"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button onClick={check} disabled={busy}
            className="mt-3 w-full text-[12px] font-bold px-3 py-2 rounded-xl bg-teal-500
                       text-white hover:bg-teal-600 disabled:opacity-60"
            data-testid="khi-screening-submit">
            {busy ? "Проверявам…" : "Покажи отговора"}
          </button>
        </>
      )}

      {result && (
        <div className="mt-3 rounded-xl p-3"
          style={{ background: color + "12", borderLeft: `3px solid ${color}` }}
          data-testid="khi-screening-result">
          <div className="text-[12px] font-bold" style={{ color }}>{result.verdict_bg}</div>
          <div className="text-[12px] mt-1 opacity-90">{result.advice_bg}</div>
          {!!result.tests_bg?.length && result.needs_testing && (
            <ul className="text-[11px] mt-1.5 list-disc list-inside opacity-80">
              {result.tests_bg.map((t) => <li key={t}>{t}</li>)}
            </ul>
          )}
          {!!result.reasons?.length && (
            <div className="text-[10px] opacity-60 mt-1.5" data-testid="khi-screening-reasons">
              Заради: {result.reasons.map((r) => r.q_bg.replace(/\?$/, "")).join(" · ")}
            </div>
          )}
          <div className="text-[9px] opacity-50 mt-1.5">{result.disclaimer_bg}</div>
        </div>
      )}
    </div>
  );
}
